import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLogin from '../profile/auth-login';
import { GOOGLE_CLIENT_ID } from '../config';

// Debug flag for authentication logging
const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === 'true';

// Helper function for debug logging
const debugLog = (...args) => {
  if (DEBUG_AUTH) {
    console.log(...args);
  }
};

// Runtime config (src/config.js). Empty (e.g. a local stack without an OAuth
// client for localhost) hides the button instead of rendering one that only alerts.
export const GOOGLE_LOGIN_ENABLED = Boolean(GOOGLE_CLIENT_ID);

const GoogleLogin = ({ isSignup = false, onGoogleSignupData = null }) => {
  const navigate = useNavigate();
  const ctx = useContext(AuthLogin);

  const handleGoogleLogin = async () => {
    try {
      const clientId = GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        console.error('Google Client ID not found in environment variables');
        alert('Google Client ID not configured. Please check your .env file.');
        return;
      }

      if (!window.google) {
        console.error('Google Identity Services not loaded');
        alert('Google Identity Services not loaded. Please refresh the page.');
        return;
      }

      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false, // Disable FedCM which can cause CORS issues
      });

      // Prompt user to sign in
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log('Google prompt failed, trying alternative method...');
          // Fallback: use renderButton method
          tryRenderButton(clientId);
        }
      });
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
      alert('Error initializing Google Sign-In. Please check your configuration.');
    }
  };

  const tryRenderButton = (clientId) => {
    try {
      // Create a temporary button container
      const tempContainer = document.createElement('div');
      tempContainer.id = 'temp-google-signin';
      tempContainer.style.position = 'fixed';
      tempContainer.style.top = '-1000px'; // Hide off-screen
      document.body.appendChild(tempContainer);

      window.google.accounts.id.renderButton(tempContainer, {
        theme: 'outline',
        size: 'large',
        text: isSignup ? 'signup_with' : 'signin_with',
        width: '300'
      });

      // Auto-click the rendered button
      setTimeout(() => {
        const button = tempContainer.querySelector('[role="button"]');
        if (button) {
          button.click();
        }
        // Clean up
        document.body.removeChild(tempContainer);
      }, 100);

    } catch (error) {
      console.error('Render button fallback failed:', error);
      alert('Google Sign-In is currently unavailable. Please try again later.');
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      const { credential } = response;
      
      if (!credential) {
        console.error('No credential found in Google response');
        alert('Google Sign-In failed: No credential received');
        return;
      }
      
      debugLog('🔍 FRONTEND DEBUG: Google response received, processing...');
      
      if (isSignup && onGoogleSignupData) {
        debugLog('🔍 FRONTEND DEBUG: Processing signup flow...');
        
        // SIGNUP FLOW: Just get user info, don't create account yet
        const apiResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tokenId: credential }),
        });

        const data = await apiResponse.json();

        if (!apiResponse.ok) {
          console.error('Google token verification failed:', data);
          throw new Error(data.error || 'Google verification failed');
        }

        debugLog('✅ FRONTEND DEBUG: Signup verification successful:', data);

        // Pass Google user data to parent component to fill form
        onGoogleSignupData({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          profilePicture: data.profilePicture,
          googleId: data.googleId
        });
        
      } else {
        debugLog('🔍 FRONTEND DEBUG: Processing login flow...');
        
        // LOGIN FLOW: Complete authentication (existing logic)
        const apiResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tokenId: credential }),
        });

        const data = await apiResponse.json();
        
        debugLog('🔍 FRONTEND DEBUG: Login API response received:');
        debugLog('  - Status:', apiResponse.status);
        debugLog('  - Success flag:', data.success);
        debugLog('  - Full response data:', JSON.stringify(data, null, 2));

        if (!apiResponse.ok) {
          console.error(`Backend authentication failed (HTTP ${apiResponse.status}, ref ${data.requestId || 'n/a'}):`, data);
          throw new Error(
            `${data.error || 'Google authentication failed'} (${apiResponse.status}${data.requestId ? `, ref ${data.requestId}` : ''})`
          );
        }

        if (data.success && data.user) {
          debugLog('🔍 FRONTEND DEBUG: Authentication successful, processing user data...');
          debugLog('  - User ID:', data.user.user_id);
          debugLog('  - Username:', data.user.username);
          debugLog('  - Full Name:', data.user.fullName);
          debugLog('  - Email:', data.user.email);
          debugLog('  - Role:', data.user.role);
          debugLog('  - isSubscribed (from API):', data.user.isSubscribed);
          debugLog('  - isFirstTimeLogin:', data.user.isFirstTimeLogin);
          debugLog('  - acceptedTerms:', data.user.acceptedTerms);
          debugLog('  - OAuth provider info:', data.user.profilePicture ? 'Has profile picture' : 'No profile picture');
          
          // Log the user in using the existing auth context
          debugLog('🔍 FRONTEND DEBUG: Calling ctx.onLogin with parameters:');
          debugLog('  - Parameter 1 (username):', data.user.username);
          debugLog('  - Parameter 2 (fullName):', data.user.fullName);
          debugLog('  - Parameter 3 (role):', data.user.role);
          debugLog('  - Parameter 4 (user_id):', data.user.user_id);
          debugLog('  - Parameter 5 (isFirstTimeLogin):', data.user.isFirstTimeLogin);
          debugLog('  - Parameter 6 (acceptedTerms):', data.user.acceptedTerms);
          debugLog('  - Parameter 7 (password):', null);
          debugLog('  - Parameter 8 (isSubscribed):', data.user.isSubscribed);
          
          ctx.onLogin(
            data.user.username,
            data.user.fullName,
            data.user.role,
            data.user.user_id,
            data.user.isFirstTimeLogin,
            data.user.acceptedTerms,
            null, // No password for OAuth users
            data.user.isSubscribed
          );

          debugLog('✅ FRONTEND DEBUG: Login successful, navigating to dashboard...');
          
          // Navigate to dashboard
          navigate('/my-dashboard');
        } else if (data.requiresSignup && data.googleUserData) {
          debugLog('🔍 FRONTEND DEBUG: User not found, redirecting to signup...');
          debugLog('  - Google user data:', data.googleUserData);
          
          // Show user feedback before redirecting
          alert('No account found with this Google account. You will be redirected to the signup page where your information will be pre-filled.');
          
          // Redirect to signup page with Google user data
          navigate('/signup', {
            state: {
              googleUserData: data.googleUserData,
              isGoogleSignup: true
            }
          });
        } else {
          console.error('❌ FRONTEND DEBUG: Authentication response missing required data:', data);
          if (DEBUG_AUTH) {
            console.error('  - data.success:', data.success);
            console.error('  - data.requiresSignup:', data.requiresSignup);
            console.error('  - data.user exists:', !!data.user);
            console.error('  - data.googleUserData exists:', !!data.googleUserData);
            if (data.user) {
              console.error('  - Missing fields in user object:');
              const requiredFields = ['user_id', 'username', 'fullName', 'role', 'isSubscribed'];
              requiredFields.forEach(field => {
                if (data.user[field] === undefined) {
                  console.error(`    - Missing: ${field}`);
                }
              });
            }
          }
          
          // Show appropriate error message
          if (data.requiresSignup) {
            alert('No account found. Please sign up first.');
          } else {
            alert('Authentication failed: Invalid response from server');
          }
        }
      }
    } catch (error) {
      console.error('❌ FRONTEND ERROR: Google authentication error:', error);
      if (DEBUG_AUTH) {
        console.error('❌ FRONTEND ERROR: Error stack:', error.stack);
      }
      
      // Show user-friendly error message
      if (error.message.includes('fetch')) {
        alert('Network error: Could not connect to authentication server.');
      } else {
        alert(`Google Sign-In failed: ${error.message}`);
      }
    }
  };

  if (!GOOGLE_LOGIN_ENABLED) return null;

  return (
    <button
      type="button"
      className="googlebtn"
      onClick={handleGoogleLogin}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18">
        <path fill="#4285F4" d="M16.51 8.18c0-.46-.04-.9-.13-1.33H9.18v2.52h4.11c-.18.97-.72 1.79-1.54 2.31v1.91h2.5c1.46-1.34 2.3-3.31 2.3-5.64z"/>
        <path fill="#34A853" d="M9.18 16.5c2.08 0 3.82-.69 5.1-1.87l-2.5-1.91c-.69.46-1.58.73-2.6.73-2 0-3.69-1.35-4.29-3.17H2.36v1.97c1.27 2.52 3.88 4.25 6.82 4.25z"/>
        <path fill="#FBBC04" d="M4.89 10.28c-.15-.46-.24-.95-.24-1.46s.09-1 .24-1.46V5.39H2.36c-.5 1-.79 2.13-.79 3.39s.29 2.39.79 3.39l2.53-1.89z"/>
        <path fill="#EA4335" d="M9.18 3.5c1.13 0 2.14.39 2.94 1.15l2.2-2.2C12.99 1.09 11.26.5 9.18.5 6.24.5 3.63 2.23 2.36 4.75l2.53 1.97c.6-1.82 2.29-3.17 4.29-3.17z"/>
      </svg>
      {isSignup ? 'Sign up with Google' : 'Continue with Google'}
    </button>
  );
};

export default GoogleLogin; 